/* eslint-disable class-methods-use-this */
import {
  getFontVariantName,
  isFontVariantRegular,
  isFontVariantSupported,
} from '../../components/Fonts/helper';
import {
  GOOGLE_FONTS_API_KEY,
  GOOGLE_FONTS_API_URL,
  GOOGLE_FONTS_CSS_URL,
} from '../../config/env';
import { FontVariantSchema } from '../../definitions/schemas';
import { standardFonts } from './StandardFonts';
import { GoogleWebfontList, WebfontFamilySchema, WebfontList } from './types';

const DATA_ATTRIBUTE_FONT_SELECTOR = 'data-font-selector';
const DATA_ATTRIBUTE_FONT_PREVIEWS = 'data-font-previews';

export class GoogleFontsService {
  private loadedFontFamilies: string[] = [];

  constructor() {
    this.loadedFontFamilies = this.getLoadedFonts();
  }

  /**
   * Checks which font previews have already been loaded and attached to
   * the DOM's <head>, to avoid adding network load by re-fetching the files.
   * We query the <head> for the stylesheets that have been added by the font
   * selector, then extract the font family names loaded by that stylesheet.
   * So, no matter if this component is destroyed and re-mounted, if the
   * fonts are available, we don't re-fetch them
   */
  public getLoadedFonts() {
    const stylesheets = document.head.querySelectorAll(
      `link[rel="stylesheet"][${DATA_ATTRIBUTE_FONT_SELECTOR}]`
    );

    const loadedFontPreviews: string[] = [];

    stylesheets.forEach((stylesheet) => {
      const previewsAttr = stylesheet.getAttribute(
        DATA_ATTRIBUTE_FONT_PREVIEWS
      );

      loadedFontPreviews.push(...`${previewsAttr}`.split(','));
    });

    return Array.from(new Set(loadedFontPreviews).values());
  }

  /**
   * Obtains the list of all available fonts in Google Fonts API. This gives
   * us the names, variants and lots of other information that we can use
   * to build a font listing. This also converts the google webfont list
   * into an interface that can be properly used, by mapping the supported
   * variants with their names
   */
  public loadFontsList(): Promise<WebfontFamilySchema[]> {
    const url = new URL(GOOGLE_FONTS_API_URL);
    url.searchParams.append('key', GOOGLE_FONTS_API_KEY);
    url.searchParams.append('sort', 'popularity');

    return (
      fetch(url.href, { method: 'GET' })
        .then((response) => response.json())
        .then((json) => json as GoogleWebfontList)
        // enhancing the result of Google Fonts list with the standard fonts
        .then((googleWebfontList) => ({
          items: [...googleWebfontList.items, ...standardFonts],
        }))
        .then(({ items }) =>
          items.map(({ family, standard, variants }): WebfontFamilySchema => {
            const processedVariants: FontVariantSchema[] = [];

            variants.forEach((variant) => {
              const name = getFontVariantName(variant);
              if (name && isFontVariantSupported(variant)) {
                processedVariants.push({ name, value: variant });
              }
            });

            return { family, standard, variants: processedVariants };
          })
        )
    );
  }

  /**
   * Builds the URL to fetch the CSS file that contains the @font-face
   * definition of each given font family (and its variants). This avoids
   * re-fetching fonts that have already been loaded and attached to the
   * <head> as stylesheets.
   *
   * If a preview text is given, the `text` URL param is included in the
   * request, to only fetch a subset of characters form the whole webfont,
   * saving some network load.
   *
   * If a `forceReload` param is given with some font names, these will be reloaded
   * every time this method is called, no matter if they've already been loaded.
   * Use this wisely, for example: if "Lato Regular" has been loaded, but not
   * all of its variants, then by not re-fetching the font, its variants won't
   * have their fonts loaded properly (e.g. Lato Bold won't show properly).
   *
   * @warn make sure to include all characters that are going to be displayed
   * if a preview is wanted, any non-fetched characters will default to the
   * browser's default font family
   *
   * @see https://developers.google.com/fonts/docs/css2
   */
  public loadFonts(
    fonts: WebfontList,
    previewText?: string,
    forceReload?: string[]
  ): Promise<undefined> {
    const url = new URL(GOOGLE_FONTS_CSS_URL);
    const fontNames: string[] = [];

    fonts.forEach(({ family, standard = false, variants }) => {
      // don't attempt to load it if it's a standard font
      if (standard) {
        return;
      }

      /**
       * If one of the visible fonts has already been loaded, we don't include
       * it, as we don't want to re-fetch the CSS file for that font and
       * add additional network load
       */
      if (
        this.loadedFontFamilies.includes(family) &&
        !forceReload?.includes(family)
      ) {
        return;
      }

      // keeping a reference to avoid re-fetching this font in the future
      this.loadedFontFamilies.push(family);

      const variantsQuery = variants
        .map(({ value }) => (isFontVariantRegular(value) ? '400' : value))
        .join(';');

      /**
       * @see https://developers.google.com/fonts/docs/css2#individual_styles_such_as_weight
       */
      const variantsAxis =
        variantsQuery.length > 0 ? `:wght@${variantsQuery}` : '';

      url.searchParams.append('family', `${family}${variantsAxis}`);
      fontNames.push(family);
    });

    if (fontNames.length === 0) {
      return Promise.resolve(undefined);
    }

    /**
     * Generating an unique array of characters to be used as a preview text
     * for the font that's going to be loaded. This helps to reduce the size
     * of the downloaded font, as it won't download the full character set
     */
    if (previewText) {
      const previewCharacters = Array.from(
        new Set([...fontNames.join(''), ...previewText]).values()
      );

      url.searchParams.append('text', previewCharacters.join(''));
    }

    url.searchParams.append('display', 'swap');

    /**
     * We're going to fetch the CSS file returned from Google Fonts that
     * defines the font-faces for the font previews, along with the urls for
     * downloading said files; then attach it to the DOM's <head> as an
     * additional stylesheet, so the browser does its magic to load the
     * font only when it's necessary (e.g. variants are only loaded
     * when the font face is applied to a rendered element)
     */
    return new Promise((resolve) => {
      const link = document.createElement('link');

      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.href = url.href;
      link.setAttribute(DATA_ATTRIBUTE_FONT_SELECTOR, '');
      link.setAttribute(DATA_ATTRIBUTE_FONT_PREVIEWS, fontNames.join(','));

      /**
       * Once that CSS file has been loaded, we wait for a bit before
       * resolving, to allow the stylesheet to load the font files and
       * make the fonts available to display each font name with its style
       */
      link.onload = () => {
        setTimeout(() => {
          resolve(undefined);
        }, 250);
      };

      document.head.appendChild(link);
    });
  }

  /**
   * A convenience and simplified method to obtain a URL that can be used by
   * a CSS file to include custom fonts.
   *
   * For example:
   *  @import url('https://fonts.googleapis.com/css2?family=Inter&display=swap');
   */
  public getFontsCSSUrl(fonts: WebfontList): string {
    const url = new URL(GOOGLE_FONTS_CSS_URL);

    fonts.forEach(({ family, standard = false, variants }) => {
      // don't attempt to load it if it's a standard font
      if (standard) {
        return;
      }

      const variantsQuery = variants
        .map(({ value }) => (isFontVariantRegular(value) ? '400' : value))
        .join(';');

      /**
       * @see https://developers.google.com/fonts/docs/css2#individual_styles_such_as_weight
       */
      const variantsAxis =
        variantsQuery.length > 0 ? `:wght@${variantsQuery}` : '';

      url.searchParams.append('family', `${family}${variantsAxis}`);
    });

    url.searchParams.append('display', 'swap');

    return url.href;
  }
}
